import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { OCCASIONS, OCCASION_SLUGS, type Occasion } from "@/data/occasions";
import { useReveal } from "@/hooks/use-reveal";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { LOCAL_BUSINESS_SCHEMA, absoluteImageUrl } from "@/lib/schema-org";
export const Route = createFileRoute("/soirees-evenements/$type")({
  loader: ({ params }) => {
    const occasion = OCCASIONS[params.type];
    if (!occasion) throw notFound();
    return { occasion };
  },
  head: ({ loaderData }) => {
    const o = loaderData?.occasion as Occasion | undefined;
    if (!o) return { meta: [{ title: "Soirées & Événements" }] };
    return {
      meta: [
        { title: o.metaTitle },
        { name: "description", content: metaDesc(o.metaDescription) },
        { property: "og:title", content: o.metaTitle },
        { property: "og:description", content: metaDesc(o.metaDescription) },
        { property: "og:image", content: absoluteImageUrl(o.hero) },
        { property: "og:url", content: `https://dj-val.fr/soirees-evenements/${o.slug}` },
      ],
      links: [{ rel: "canonical", href: `https://dj-val.fr/soirees-evenements/${o.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: o.metaTitle,
            provider: { ...LOCAL_BUSINESS_SCHEMA },
            areaServed: ["Cavaillon", "Avignon", "Vaucluse", "Bouches-du-Rhône", "Gard"],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: o.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-dvh flex items-center justify-center bg-background text-foreground">
      <div className="text-center">
        <h1 className="font-serif-display text-4xl">Occasion introuvable</h1>
        <Link to="/soirees-evenements" className="mt-4 inline-block text-[var(--gold)] underline">Retour aux soirées</Link>
      </div>
    </div>
  ),
  component: OccasionPage,
});

function OccasionPage() {
  useReveal();
  const { occasion } = Route.useLoaderData();
  const o: Occasion = occasion;

  return (
    <div className="bg-background text-muted-foreground">
      <section className="on-dark relative min-h-[75vh] flex items-center px-6 overflow-hidden pt-24">
        <div className="absolute inset-0 z-0">
          <img loading="lazy" decoding="async" src={o.hero} alt={o.name} className="h-full w-full object-cover opacity-40 kenburns-img" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, var(--ink) 0%, color-mix(in oklab, var(--ink) 70%, transparent) 55%, color-mix(in oklab, var(--ink) 30%, transparent) 100%), linear-gradient(to bottom, var(--ink) 0%, transparent 40%, var(--ink) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-3xl reveal space-y-6">
          <p className="eyebrow">Soirées & Événements</p>
          <h1 className="font-serif-display text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <span className="italic font-light">{o.h1}</span>
          </h1>
          <span className="gold-rule !w-12" />
          <p className="text-base text-foreground/85 font-light leading-relaxed max-w-xl">{o.intro}</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Link to="/contactez-nous" className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all text-center">
              Devis gratuit
            </Link>
            <a href="tel:0767908667" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/90 hover:text-foreground transition-colors underline underline-offset-4 decoration-foreground/30">
              07 67 90 86 67
            </a>
          </div>

        </div>
      </section>
      <ReviewsProof />

      {o.answer && (
        <section className="py-16 px-6 border-b border-[var(--gold-line)]">
          <div className="max-w-3xl mx-auto sr" data-reveal>
            <p className="text-[15px] text-foreground/90 font-light leading-[1.9]">{o.answer}</p>
          </div>
        </section>
      )}



      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Ce que je vous apporte</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">3 piliers pour une soirée réussie</h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-px bg-[var(--gold-line)] md:grid-cols-3 border border-[var(--gold-line)]">
          {o.pillars.map((p, i) => (
            <div key={p.title} className="bg-background p-10 sr" data-reveal style={{ transitionDelay: `${i * 80}ms` }}>
              <p className="font-serif-display italic text-[var(--gold)] text-2xl">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="font-serif-display text-2xl text-foreground mt-4">{p.title}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {o.sections?.map((s) => (
        <section key={s.h2} className="py-20 px-6 border-t border-[var(--gold-line)]">
          <div className="max-w-3xl mx-auto sr" data-reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">{s.h2}</h2>
            <span className="gold-rule !w-12 my-8" />
            <div className="space-y-5">
              {s.paragraphs.map((p, i) => (
                <p key={i} className="text-[15px] text-muted-foreground font-light leading-[1.9]">{p}</p>
              ))}
            </div>
          </div>
        </section>
      ))}

      {o.relatedLinks && o.relatedLinks.length > 0 && (
        <section className="py-16 px-6 border-t border-[var(--gold-line)]">
          <div className="max-w-4xl mx-auto text-center">
            <p className="eyebrow text-[var(--gold)]">À voir aussi</p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {o.relatedLinks.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  params={l.params as never}
                  className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/85 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}



      <section className="py-24 px-6 max-w-4xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Questions fréquentes</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">{o.name}</h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="space-y-4">
          {o.faq.map((f) => (
            <details key={f.q} className="group border border-[var(--gold-line)] bg-card p-6 sr" data-reveal>
              <summary className="cursor-pointer font-serif-display text-xl text-foreground flex justify-between items-center gap-4">
                {f.q}
                <span className="text-[var(--gold)] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="py-20 px-6 border-t border-[var(--gold-line)] bg-card">
        <div className="max-w-6xl mx-auto text-center">
          <p className="eyebrow text-[var(--gold)]">Autres prestations</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {OCCASION_SLUGS.filter((s) => s !== o.slug).map((s) => (
              <Link key={s} to="/soirees-evenements/$type" params={{ type: s }} className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/85 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
                {OCCASIONS[s].name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
